/**
 * LoyaltyProgramPage · v2.7.65+E257 (E259 · shared card)
 * ======================================================
 * Public "Customer Loyalty Program" page at /loyalty. Turns the hero promo
 * ("Get your 10th ride FREE in the City of Weatherford") into a full page.
 *
 * Logged-in riders see a live PUNCH-CARD tracker of their completed rides
 * (fetched from GET /api/customer/loyalty) — e.g. "7 / 10 · 3 rides until your
 * free ride". Anonymous visitors see a Sign-in CTA. The card itself is the
 * shared <LoyaltyPunchCard> (also used inside the Rider dashboard).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Car, Gift, ArrowRight, Loader2, Star, Sparkles, LogIn } from "lucide-react";
import customerApi from "@/lib/customerApi";
import { useCustomer } from "@/contexts/CustomerContext";
import { LoyaltyPunchCard } from "@/components/LoyaltyPunchCard";

const AMBER = "#fbbf24";

export default function LoyaltyProgramPage() {
  const ctx = useCustomer();
  const customer = ctx?.customer || null;
  const [status, setStatus] = useState("loading"); // loading | anon | ok
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    customerApi
      .get("/customer/loyalty")
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setStatus("ok");
      })
      .catch(() => {
        if (!cancelled) setStatus("anon");
      });
    return () => { cancelled = true; };
  }, []);

  const freeEvery = data?.free_every || 10;

  return (
    <MarketingLayout>
      <SeoHead
        title="Customer Loyalty Program — Buller Taxi Services"
        description="Ride Hard. Reap the Rewards. Get your 10th ride FREE in the City of Weatherford with the Buller Taxi customer loyalty program. Track your rides in your Rider Portal."
        canonical="/loyalty"
      />

      {/* ── HERO BAND ─────────────────────────────────────────────── */}
      <section className="bg-zinc-950 border-b border-amber-400/40 py-16 sm:py-20" data-testid="loyalty-hero">
        <div className="max-w-5xl mx-auto px-5 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] font-bold mb-3" style={{ color: AMBER }}>
            <Sparkles className="h-3.5 w-3.5"/> Customer Loyalty Program
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight" style={{ color: "#f4f4f5" }} data-testid="loyalty-title">
            Ride Hard. <span style={{ color: AMBER }}>Reap the Rewards.</span>
          </h1>
          <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: "#d4d4d8" }}>
            Every ride gets you closer to a free one. Get your <span className="font-black" style={{ color: AMBER }}>{freeEvery}th ride FREE</span> in the City of Weatherford.
          </p>
        </div>
      </section>

      {/* ── TRACKER ───────────────────────────────────────────────── */}
      <section className="bg-zinc-950 py-14" data-testid="loyalty-tracker-section">
        <div className="max-w-4xl mx-auto px-5">
          {status === "loading" && (
            <div className="text-center py-10" data-testid="loyalty-loading">
              <Loader2 className="h-7 w-7 animate-spin mx-auto" style={{ color: AMBER }}/>
            </div>
          )}

          {status === "anon" && (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center" data-testid="loyalty-anon">
              <div className="h-14 w-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "rgba(251,191,36,0.15)", border: `2px solid ${AMBER}` }}>
                <Gift className="h-7 w-7" style={{ color: AMBER }}/>
              </div>
              <h2 className="text-xl md:text-lg font-black" style={{ color: "#f4f4f5" }}>Sign in to track your rewards</h2>
              <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "#a1a1aa" }}>
                Log in to your Rider Portal to see how many rides you have left until your next free ride.
              </p>
              <div className="flex flex-wrap gap-3 justify-center mt-6">
                <Link to="/ride/login" data-testid="loyalty-anon-signin">
                  <Button className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider">
                    <LogIn className="h-4 w-4 mr-1.5"/> Sign In / Sign Up
                  </Button>
                </Link>
                <Link to="/ride/login" data-testid="loyalty-anon-book">
                  <Button variant="outline" className="bg-transparent border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-zinc-950 font-extrabold uppercase tracking-wider">
                    Book a Ride <ArrowRight className="h-4 w-4 ml-1"/>
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === "ok" && data && (
            <LoyaltyPunchCard data={data} name={customer?.name || customer?.first_name || "Rider"}/>
          )}
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────── */}
      <section className="bg-zinc-950 border-t border-zinc-900 py-16" data-testid="loyalty-how">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: AMBER }}>Simple &amp; Rewarding</div>
            <h2 className="text-3xl sm:text-4xl font-black mt-2" style={{ color: "#f4f4f5" }}>How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Car, title: "1 · Ride with us", desc: "Book and complete rides through your Rider Portal or by calling dispatch. Every completed ride counts." },
              { icon: Star, title: "2 · Collect stamps", desc: `Each ride earns a stamp on your loyalty card. Watch your progress toward ride #${freeEvery}.` },
              { icon: Gift, title: `3 · Ride #${freeEvery} is FREE`, desc: "Your 10th ride is on us — free within the City of Weatherford. Then the card starts fresh." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900 p-6" data-testid={`loyalty-step-${i}`}>
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(251,191,36,0.15)", border: `2px solid ${AMBER}` }}>
                    <Icon className="h-6 w-6" style={{ color: AMBER }}/>
                  </div>
                  <h3 className="text-lg font-extrabold" style={{ color: "#f4f4f5" }}>{s.title}</h3>
                  <p className="text-sm mt-1.5 leading-relaxed" style={{ color: "#a1a1aa" }}>{s.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Link to="/ride/login" data-testid="loyalty-cta-book">
              <Button className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider">
                Book a Ride <ArrowRight className="h-4 w-4 ml-1.5"/>
              </Button>
            </Link>
            <p className="text-xs mt-4" style={{ color: "#71717a" }}>
              Free ride applies to trips within the City of Weatherford. Ask dispatch for details.
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
